import { createSelector } from 'reselect'
import { take } from 'lodash'
import { RootStoreState } from '@src/models/app.types'
import { CoachesCoachingKind } from '@src/types'
import { getLatestCoachSwitchCancelledAppointment } from './selectors/getLatestCoachSwitchCancelledAppointment'

export const nutritionistHubStateSelector = (state: RootStoreState) => state.nutritionistHub

export const latestUserEhrAppointmentSelector = createSelector(
  nutritionistHubStateSelector,
  (nutritionistHubStore) => {
    return nutritionistHubStore.latestUserEhrAppointment
  },
)

export const ehrAppointmentSchedulingInfoSelector = createSelector(
  nutritionistHubStateSelector,
  (nutritionistHubStore) => {
    return nutritionistHubStore.ehrAppointmentSchedulingInfo
  },
)

export const insurancePolicySelector = createSelector(
  nutritionistHubStateSelector,
  (nutritionistHubStore) => nutritionistHubStore.insurancePolicy,
)

export const priorityOrderedCoachAssignmentsSelector = createSelector(
  nutritionistHubStateSelector,
  (nutritionistHubStore) => nutritionistHubStore.priorityOrderedCoachAssignments,
)

export const coachAssignmentForCoachingKindSelector = (coachingKind: CoachesCoachingKind) =>
  createSelector(nutritionistHubStateSelector, (nutritionistHubStore) =>
    nutritionistHubStore.priorityOrderedCoachAssignments?.find(
      (assignment) => assignment.coachingKind.key === coachingKind,
    ),
  )

export const topPriorityCoachAssignmentSelector = createSelector(
  nutritionistHubStateSelector,
  (nutritionistHubStore) =>
    nutritionistHubStore.priorityOrderedCoachAssignments?.find(
      (assignment) => assignment.coachingKind.primary,
    ) ?? nutritionistHubStore.topPriorityCoachAssignment,
)

export const allMemberNotesSelector = createSelector(
  nutritionistHubStateSelector,
  (nutritionistHubStore) => nutritionistHubStore.allMemberNotes,
)

export const memberNotesUpdatedAtTimestampSelector = createSelector(
  nutritionistHubStateSelector,
  (nutritionistHubStore) => nutritionistHubStore.memberNotesUpdatedAt,
)

export const pinnedMemberNotesSelector = createSelector(allMemberNotesSelector, (allMemberNotes) =>
  allMemberNotes.filter((note) => !!note.pinnedAt),
)

export const unpinnedMemberNoteSelector = createSelector(allMemberNotesSelector, (allMemberNotes) =>
  allMemberNotes.filter((note) => !note.pinnedAt),
)

export const noteSelector = (noteId: string) =>
  createSelector(allMemberNotesSelector, (notes) => {
    return notes.find((note) => note.id === noteId)
  })

export const latestUnpinnedMemberNoteSelector = createSelector(
  unpinnedMemberNoteSelector,
  (unpinnedNotes) => unpinnedNotes[0] as typeof unpinnedNotes[0] | undefined,
)

export const topMemberNotesSelector = createSelector(
  pinnedMemberNotesSelector,
  unpinnedMemberNoteSelector,
  (pinnedNotes, lastNotes) => {
    // we always want to show only the last unpinned note
    const takeCount = pinnedNotes.length + 1
    return take([...pinnedNotes, ...lastNotes], takeCount)
  },
)

export const allEhrAppointmentsSelector = createSelector(
  nutritionistHubStateSelector,
  (nutritionistHubStore) => nutritionistHubStore.allEhrAppointments,
)

export const upcomingEhrAppointmentsSelector = createSelector(
  nutritionistHubStateSelector,
  (nutritionistHubStore) => nutritionistHubStore.upcomingEhrAppointments,
)

export const pastEhrAppointmentsSelector = createSelector(
  nutritionistHubStateSelector,
  (nutritionistHubStore) => nutritionistHubStore.pastEhrAppointments,
)

export const latestCoachSwitchCancelledEhrAppointmentsSelector = createSelector(
  nutritionistHubStateSelector,
  (nutritionistHubStore) =>
    getLatestCoachSwitchCancelledAppointment(nutritionistHubStore.allEhrAppointments),
)

export const pendingEhrAppointmentsSelector = createSelector(
  nutritionistHubStateSelector,
  (nutritionistHubStore) => nutritionistHubStore.pendingEhrAppointments,
)

export const ehrSuggestedAvailabilitySelector = createSelector(
  nutritionistHubStateSelector,
  (nutritionistHubStore) => nutritionistHubStore.ehrSuggestedAvailability,
)

export const appointmentRecurrenceSelector = createSelector(
  nutritionistHubStateSelector,
  (nutritionistHubStore) => nutritionistHubStore.appointmentRecurrence,
)

export const nextRecurringAppointmentSlotSelector = createSelector(
  nutritionistHubStateSelector,
  (nutritionistHubStore) => nutritionistHubStore.nextRecurringAppointmentSlot,
)

export const coachingCallAllowanceSelector = createSelector(
  nutritionistHubStateSelector,
  (nutritionistHubStore) => nutritionistHubStore.coachingCallAllowance,
)

export const insuranceCardSubmissionSelector = createSelector(
  nutritionistHubStateSelector,
  (nutritionistHubStore) => nutritionistHubStore.insuranceCardSubmission,
)

export const ehrEarlierAvailableSlotsByAppointmentIdSelector = createSelector(
  nutritionistHubStateSelector,
  (nutritionistHubStore) => nutritionistHubStore.ehrEarlierAvailableSlotsByAppointmentId || {},
)

export const latestUserEhrAppointmentEarlierAvailableSlotsSelector = createSelector(
  [latestUserEhrAppointmentSelector, ehrEarlierAvailableSlotsByAppointmentIdSelector],
  (latestUserAppointment, earlierAvailableSlotsByAppointmentId) => {
    if (!latestUserAppointment) {
      return []
    }

    return earlierAvailableSlotsByAppointmentId[latestUserAppointment.id] || []
  },
)

export const allCoachProfilesSelector = createSelector(
  nutritionistHubStateSelector,
  (nutritionistHubStore) => nutritionistHubStore.coachProfiles,
)

export const coachProfileSelector = createSelector(
  nutritionistHubStateSelector,
  (nutritionistHubStore) => nutritionistHubStore.coachProfile,
)
